import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
  Image,
} from '@chakra-ui/react';

import {
  useGetProjectByIdQuery,
  useGetPhaseByIdQuery,
  useGetDirectoryByIdQuery,
} from '../../services';
import { ReactComponent as ChevronRightIcon } from '../../assets/icons/chevron-right.svg';
import {
  getProjectType,
  multiplePhaseTabs,
  phaseDetailsTabs,
  singlePhaseTabs,
} from './utils';

export const ProjectsLayout = () => {
  const location = useLocation();
  const params = useParams();
  const { projectId, phaseId, tabName, directoryId } = params;

  let type = location.pathname.split('/')[2];

  const { data: savedProject } = useGetProjectByIdQuery(Number(projectId), {
    skip: !projectId,
  });

  const { data: currentDirectory } = useGetDirectoryByIdQuery(
    Number(directoryId),
    { skip: !directoryId }
  );

  const { data: savedPhase } = useGetPhaseByIdQuery(
    {
      projectId: Number(projectId),
      phaseId: Number(phaseId),
    },
    {
      skip: !projectId || !phaseId,
    }
  );

  return (
    <Box mx="8" h="full" display="flex" flexDirection="column">
      <Breadcrumb separator={<ChevronRightIcon />}>
        <BreadcrumbItem>
          <HStack fontSize="xs" color="custom_blue.1">
            <Image
              src="/sidebar/projects.svg"
              alt="projects icon"
              boxSize="4"
            />
            <Box>Projects</Box>
          </HStack>
        </BreadcrumbItem>

        <BreadcrumbItem isCurrentPage={location.pathname.endsWith(type)}>
          <BreadcrumbLink
            as={Link}
            to={`/projects/${type}`}
            fontSize="xs"
            color="custom_blue.1"
            textTransform="capitalize"
          >
            {type ? type : 'All'} Projects
          </BreadcrumbLink>
        </BreadcrumbItem>

        {(location.pathname.includes('new-project') || projectId) && (
          <BreadcrumbItem
            isCurrentPage={
              location.pathname.endsWith('new-project') ||
              location.pathname.endsWith(`${String(projectId)}/project-details`)
            }
          >
            {projectId ? (
              <BreadcrumbLink
                as={Link}
                to={`/projects/${type}/${projectId}/project-details`}
                fontSize="xs"
                color="custom_blue.1"
              >
                {savedProject?.data.projectName}
              </BreadcrumbLink>
            ) : (
              <BreadcrumbLink
                as={Link}
                to={`/projects/${type}/new-project`}
                fontSize="xs"
                color="custom_blue.1"
              >
                Add New Project
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
        )}

        {projectId && tabName && location.pathname.includes('single-phase') && (
          <BreadcrumbItem isCurrentPage={location.pathname.endsWith(tabName)}>
            <BreadcrumbLink
              as={Link}
              to={`/projects/single-phase/${projectId}/project-details/${tabName}`}
              fontSize="xs"
              color="custom_blue.1"
            >
              {singlePhaseTabs.find(tab => tab.link === tabName)?.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
        )}

        {(location.pathname.includes('new-phase') || phaseId) && (
          <BreadcrumbItem
            isCurrentPage={
              location.pathname.endsWith('new-phase') ||
              location.pathname.endsWith(`phases/${String(phaseId)}`)
            }
          >
            {phaseId ? (
              <BreadcrumbLink
                as={Link}
                to={`/projects/multiple-phase/${String(
                  projectId
                )}/project-details/phases/${phaseId}`}
                fontSize="xs"
                color="custom_blue.1"
              >
                {savedPhase?.data.phaseName}
              </BreadcrumbLink>
            ) : (
              <BreadcrumbLink
                as={Link}
                to={`/projects/multiple-phase/${String(
                  projectId
                )}/project-details/phases/new-phase`}
                fontSize="xs"
                color="custom_blue.1"
              >
                Add New Phase
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
        )}

        {location.pathname.includes('phases/update') && phaseId && (
          <BreadcrumbItem
            isCurrentPage={location.pathname.endsWith(
              `phases/update/${String(phaseId)}`
            )}
          >
            <BreadcrumbLink
              as={Link}
              to={`/projects/multiple-phase/${String(
                projectId
              )}/project-details/phases/update/${phaseId}`}
              fontSize="xs"
              color="custom_blue.1"
            >
              Update Phase
            </BreadcrumbLink>
          </BreadcrumbItem>
        )}

        {projectId &&
          tabName &&
          !phaseId &&
          location.pathname.includes('multiple-phase') && (
            <BreadcrumbItem isCurrentPage={location.pathname.endsWith(tabName)}>
              <BreadcrumbLink
                as={Link}
                to={`/projects/multiple-phase/${projectId}/project-details/${tabName}`}
                fontSize="xs"
                color="custom_blue.1"
              >
                {multiplePhaseTabs.find(tab => tab.link === tabName)?.name}
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}

        {projectId &&
          tabName &&
          phaseId &&
          location.pathname.includes('multiple-phase') && (
            <BreadcrumbItem isCurrentPage={location.pathname.endsWith(tabName)}>
              <BreadcrumbLink
                as={Link}
                to={`/projects/multiple-phase/${projectId}/project-details/phases/${phaseId}/${tabName}`}
                fontSize="xs"
                color="custom_blue.1"
              >
                {phaseDetailsTabs.find(tab => tab.link === tabName)?.name}
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}

        {projectId &&
          tabName &&
          directoryId &&
          location.pathname.endsWith(directoryId) && (
            <BreadcrumbItem
              isCurrentPage={location.pathname.endsWith(directoryId)}
            >
              <BreadcrumbLink
                as={Link}
                to={`/projects/${getProjectType()}/${projectId}/project-details/${
                  location.pathname.includes('single-phase')
                    ? ''
                    : `phases/${String(phaseId)}`
                }${tabName}/${directoryId}`}
                fontSize="xs"
                color="custom_blue.1"
              >
                {currentDirectory?.data.directoryName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}

        {projectId &&
          tabName &&
          (location.pathname.includes('new-directory') ||
            location.pathname.includes('new-document')) &&
          directoryId && (
            <BreadcrumbItem
              isCurrentPage={
                location.pathname.endsWith('new-directory') ||
                location.pathname.endsWith('new-document')
              }
            >
              <BreadcrumbLink
                as={Link}
                to={`/projects/${type}/${projectId}/project-details/${
                  getProjectType() === 'single-phase'
                    ? ''
                    : `phases/${String(phaseId)}/`
                }${tabName}/${directoryId}/${
                  location.pathname.includes('new-directory')
                    ? 'new-directory'
                    : 'new-document'
                }`}
                fontSize="xs"
                color="custom_blue.1"
              >
                Add New Document
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}

        {projectId &&
          tabName &&
          location.pathname.includes('update-directory') &&
          directoryId && (
            <BreadcrumbItem
              isCurrentPage={location.pathname.endsWith('update-directory')}
            >
              <BreadcrumbLink
                as={Link}
                to={`/projects/${type}/${projectId}/project-details/${
                  type === 'single-phase' ? '' : `phases/${String(phaseId)}`
                }${tabName}/${directoryId}/update-directory`}
                fontSize="xs"
                color="custom_blue.1"
              >
                Update Document
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}
      </Breadcrumb>

      <Outlet />
    </Box>
  );
};
